/**
 * Part Schedule (isolated module).
 *
 * Read-only list of part-level firm-boundary entries imported from the
 * customer's ERP / middleware. The page is gated by the 'part_schedule'
 * feature flag at nav level; the backend ViewSet additionally gates by
 * the per-customer CustomerImportProfile.part_schedule_module_enabled
 * flag, so if the page is ever reached by a customer that has the module
 * disabled, it renders empty (no error).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Loader2, Search, CalendarClock, Download } from 'lucide-react';
import SupplierMultiSelect from '../components/purchasing/SupplierMultiSelect';
import { toast } from 'sonner';
import { purchasingAPI } from '../lib/api';
import type { PartScheduleEntry, QueryParams } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import {
  SortableHead,
  PaginationControls,
  type SortKey,
} from '../components/part-schedule/PartScheduleTableControls';

const PAGE_SIZE_OPTIONS = [50, 200, 500] as const;
const DEFAULT_PAGE_SIZE = 50;

export default function PartSchedulePage() {
  const { formatDate } = useFormatters();
  const [rows, setRows] = useState<PartScheduleEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [debouncedSupplierSearch, setDebouncedSupplierSearch] = useState('');
  // Supplier column quick-filter: multi-select of exact supplier names from
  // the options endpoint, plus '__none__' (empty Supplier column). Empty
  // array = all suppliers. Sent JSON-encoded — names may contain commas, so
  // a delimiter-joined param could never round-trip safely.
  const [supplierFilter, setSupplierFilter] = useState<string[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const supplierFilterKey = supplierFilter.length ? JSON.stringify(supplierFilter) : '';
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sortKey, setSortKey] = useState<SortKey>('part_number');
  const [sortDesc, setSortDesc] = useState(false);

  // Ties broken by plant so a part's plant rows stay together, matching the
  // server default (one row per part *and* plant).
  const ordering = useMemo(() => {
    const prefix = sortDesc ? '-' : '';
    return sortKey === 'part_number'
      ? `${prefix}part_number,plant`
      : `${prefix}${sortKey},part_number`;
  }, [sortKey, sortDesc]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDesc(d => !d);
    } else {
      setSortKey(key);
      setSortDesc(false);
    }
    setPage(1);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSupplierSearch(supplierSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [supplierSearch]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Race guard: progressive typing fires a request per debounce tick and a
  // broader earlier query can return after the newest one — stale rows must
  // never overwrite the correct narrower result.
  const fetchRequestRef = useRef(0);

  const fetchRows = useCallback(async () => {
    const requestId = ++fetchRequestRef.current;
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
        ordering,
      };
      if (debouncedSearch) params['search'] = debouncedSearch;
      if (debouncedSupplierSearch) params['supplier_search'] = debouncedSupplierSearch;
      if (supplierFilterKey) params['supplier_filter'] = supplierFilterKey;
      if (activeFilter === 'active') params['active'] = true;
      if (activeFilter === 'inactive') params['active'] = false;

      const res = await purchasingAPI.getPartScheduleEntries(params);
      if (requestId !== fetchRequestRef.current) return; // superseded
      setRows(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch (err: unknown) {
      if (requestId !== fetchRequestRef.current) return;
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to load part schedule: ${msg}`);
      setRows([]);
      setTotal(0);
    } finally {
      if (requestId === fetchRequestRef.current) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, debouncedSupplierSearch, supplierFilterKey, activeFilter, ordering]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Supplier quick-filter options — loaded once; same 2-year delivery rule
  // as the Supplier column, so the dropdown always mirrors what the column
  // can actually show.
  useEffect(() => {
    purchasingAPI.getPartScheduleSupplierOptions()
      .then(res => setSupplierOptions(res.data.suppliers ?? []))
      .catch(() => setSupplierOptions([]));
  }, []);

  const handleExport = async (exportAll: boolean) => {
    setExporting(true);
    try {
      // Mirror the list params so the file matches the screen. When exporting
      // everything the server ignores them, but sending them is harmless and
      // keeps the two call sites identical.
      const params: QueryParams & { export_all?: boolean } = { ordering };
      if (exportAll) {
        params.export_all = true;
      } else {
        if (debouncedSearch) params['search'] = debouncedSearch;
        if (debouncedSupplierSearch) params['supplier_search'] = debouncedSupplierSearch;
        if (supplierFilterKey) params['supplier_filter'] = supplierFilterKey;
        if (activeFilter === 'active') params['active'] = true;
        if (activeFilter === 'inactive') params['active'] = false;
      }

      const res = await purchasingAPI.exportPartScheduleEntries(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `part_schedule_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(exportAll ? 'Exported all entries.' : 'Exported filtered entries.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  const activeSummary = useMemo(() => {
    const activeCount = rows.filter(r => r.active).length;
    return { active: activeCount, inactive: rows.length - activeCount };
  }, [rows]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <CalendarClock className="h-6 w-6" />
          Part Schedule
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Part-level firm-boundary configuration imported from the ERP. One
          entry per part and plant; firm-boundary values drive how far in the
          future a PO line counts as firm vs forecast.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs font-medium text-muted-foreground">
                Search by part number, description or supplier part no
              </label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Part number, description or supplier part no…"
                  className="pl-8"
                />
              </div>
            </div>
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs font-medium text-muted-foreground">
                Search by supplier
              </label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={supplierSearch}
                  onChange={e => { setSupplierSearch(e.target.value); setPage(1); }}
                  placeholder="Supplier name…"
                  className="pl-8"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Supplier</label>
              <SupplierMultiSelect
                options={supplierOptions}
                selected={supplierFilter}
                onChange={sel => { setSupplierFilter(sel); setPage(1); }}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select
                value={activeFilter}
                onValueChange={v => {
                  setActiveFilter(v as 'all' | 'active' | 'inactive');
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[160px] mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Entries</CardTitle>
          <div className="flex items-center gap-3">
            <div className="text-xs text-muted-foreground">
              {total.toLocaleString()} total · {activeSummary.active.toLocaleString()} active on this page
            </div>
            <Select
              value={String(pageSize)}
              onValueChange={v => { setPageSize(Number(v)); setPage(1); }}
            >
              <SelectTrigger className="w-[130px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(size => (
                  <SelectItem key={size} value={String(size)}>
                    {size} per page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={exporting}>
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport(false)}>
                  Export filtered ({total.toLocaleString()})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport(true)}>
                  Export all entries
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {total > pageSize && !loading && rows.length > 0 && (
            <div className="mb-4">
              <PaginationControls
                page={page}
                totalPages={totalPages}
                disabled={loading}
                onChange={setPage}
              />
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">
              No part schedule entries found.
              {debouncedSearch && ' Try clearing your search.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {(
                    [
                      ['Part Number', 'part_number'],
                      ['Description', 'description'],
                    ] as [string, SortKey][]
                  ).map(([label, key]) => (
                    <SortableHead
                      key={key}
                      label={label}
                      sortKey={key}
                      currentSort={sortKey}
                      descending={sortDesc}
                      onSort={handleSort}
                    />
                  ))}
                  {/* Multi-valued when a part is dual-sourced — nothing single to sort by. */}
                  <TableHead>Supplier</TableHead>
                  <TableHead>Supplier Part No</TableHead>
                  <SortableHead
                    label="Schedule Firm (days)"
                    sortKey="schedule_firm"
                    currentSort={sortKey}
                    descending={sortDesc}
                    onSort={handleSort}
                    className="text-right"
                  />
                  {(
                    [
                      ['Plant', 'plant'],
                      ['Status', 'active'],
                    ] as [string, SortKey][]
                  ).map(([label, key]) => (
                    <SortableHead
                      key={key}
                      label={label}
                      sortKey={key}
                      currentSort={sortKey}
                      descending={sortDesc}
                      onSort={handleSort}
                    />
                  ))}
                  <TableHead>Source</TableHead>
                  <SortableHead
                    label="Last Updated"
                    sortKey="updated_at"
                    currentSort={sortKey}
                    descending={sortDesc}
                    onSort={handleSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-sm">{row.part_number}</TableCell>
                    <TableCell className="text-sm max-w-[360px] whitespace-normal break-words">
                      {row.description || '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.suppliers && row.suppliers.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {row.suppliers.map(name => (
                            <Badge key={name} variant="secondary" className="font-normal">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.supplier_part_numbers && row.supplier_part_numbers.length > 0
                        ? row.supplier_part_numbers.join(', ')
                        : <span className="text-muted-foreground font-sans text-sm">—</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.schedule_firm ?? '—'}
                    </TableCell>
                    <TableCell>{row.plant || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={row.active ? 'default' : 'outline'}>
                        {row.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.source || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(row.updated_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > pageSize && (
            <div className="mt-4">
              <PaginationControls
                page={page}
                totalPages={totalPages}
                disabled={loading}
                onChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
